import { Menu, MessageCircle, ChevronRight, ShoppingCart } from 'lucide-react';
import { toast } from 'sonner';
import { appendOrderNotes } from '@/lib/orderNotesStorage';
import {
  buildFreeTextAnswer,
  detectFrustrationLevel,
  frustrationPrefix,
  inferServicePreferenceFromText,
  normalizeAssistantText,
} from '@/lib/assistantFreeText';
import { formatOrderNoteFromCustomizationSnippet } from '@/lib/assistant/assistantProducts';
import {
  parseStaffTakeOrderText,
  looksLikeStaffOrderPaste,
  staffParsedToCheckoutPrefill,
  ORDER_LINE_HINT_RX,
} from '@/lib/orders/staffTakeOrder';
import { promoCatalogItemToCartProduct } from '@/lib/menu/catalogPromos';
import { parseCompositeOrderText } from '@/lib/assistant/parseCompositeOrderText';
import { hasOrderInstructionIntent } from '@/lib/assistant/assistantProducts';
import {
  matchesOrderCorrectionIntent,
  matchesOrderDetailRequest,
  matchesCheckoutReadyIntent,
} from '@/lib/assistant/assistantOrderIntents';
import {
  inferAssistantIntent,
  trackAssistantEvent,
} from '@/lib/assistantAnalytics';
import { buildContextualNudge } from '@/lib/assistantContextualNudge';
import { createAssistantFallbackOptions } from '@/lib/assistantFallbackOptions';
import { ASSISTANT_SERVICE_PREF_KEY } from '@/lib/assistant/assistantProfileUtils';
import { openWhatsAppUrl } from '@/lib/checkoutWhatsAppOrder';
import { logStructured } from '@/lib/networkLog';
import {
  isDemoBusinessId,
  buildDemoAssistantFreeTextAnswer,
} from '@/lib/demo/demoBusiness';
import { trackEvent, trackAssistantUsed } from '@/lib/analytics/trackEvent';
import { updateSessionMemoryFromMessage } from './assistantChatSession';
import {
  createEmptyGuidedOrderDraft,
  processGuidedOrderTurn,
  looksLikeProductOrderLine,
} from '@/lib/assistant/assistantGuidedOrderFlow';
import { hasCompositeOrderIntent } from '@/lib/assistant/assistantRegionalSpanish';

/** Búsqueda por texto libre, fallback y nudge silencioso. */
export function bindAssistantChatSearch(ctx) {
  const {
    businessId,
    menuData,
    currentStep,
    freeTextInput,
    setFreeTextInput,
    sessionMemoryRef,
    messagesRef,
    lastSuggestedProductIdsRef,
    silentNudgeTimeoutRef,
    demoAssistantUsedRef,
    setMessages,
    getCartCount,
    assistantAnalyticsCtx,
    addBotMessage,
    addUserMessage,
    proceedToCheckoutRef,
    showMainMenuRef,
    showMainMenu,
    showCheckoutPreferencePrompt,
    showCartSummaryInChat,
    showOrderDetailAndFollowUp,
    handleOrderCloseFreeText,
    handleMainChoiceFromText,
    handleOrderChoice,
    handleOrderChoiceFromText,
    touchCheckoutFromAssistant,
    openAssistantCheckout,
    addToCart,
    clearCart,
    buildCheckoutPreview,
    setCurrentStep,
    showOrderOptions,
  } = ctx;

  const MENU_INTENT_RX =
    /\b(mand(a|e)\s+menu|muestra(r)?\s+(el\s+)?menu|ver\s+(el\s+)?menu|no\s?s[eé]\s+que\s+pedir|nose\s+que\s+pedir)\b/i;
  const KNOW_ORDER_INTENT_RX = /\b(s[eé]\s+que\s+pedir|ya\s+s[eé]\s+que\s+pedir)\b/i;

  const applyStaffParsedOrder = (staffParsed) => {
    if (typeof clearCart === 'function') clearCart();
    staffParsed.cartItems.forEach((item) => {
      const product = item.product?.is_catalog_promo
        ? promoCatalogItemToCartProduct(item.product)
        : item.product;
      addToCart(product, item.addOns || [], item.removedIngredients || [], item.quantity, {
        businessId,
        silent: true,
      });
    });
    staffParsed.unmatchedNotes?.forEach((snippet) => {
      appendOrderNotes(businessId, formatOrderNoteFromCustomizationSnippet(snippet));
    });
    const prefill = staffParsedToCheckoutPrefill(staffParsed);
    sessionMemoryRef.current.assistantEntryMode = null;
    sessionMemoryRef.current.guidedOrderActive = false;
    sessionMemoryRef.current.guidedOrderDraft = null;
    if (staffParsed.unmatchedNotes?.length) {
      addBotMessage(
        `Anoté también: ${staffParsed.unmatchedNotes.join('; ')}. Si algo no está en la carta, lo dejamos en el pedido.`
      );
    }
    showOrderDetailAndFollowUp(prefill);
  };

  const applyCompositeParsedOrder = (composite) => {
    const compositeLineCount = composite.lines?.length ?? 0;
    composite.lines?.forEach(({ product, qty, removedIngredients, note }) => {
      addToCart(product, [], removedIngredients, qty, { businessId, silent: true });
      if (note) {
        appendOrderNotes(businessId, formatOrderNoteFromCustomizationSnippet(note));
      }
    });
    composite.unmatchedNotes?.forEach((snippet) => {
      appendOrderNotes(businessId, formatOrderNoteFromCustomizationSnippet(snippet));
    });

    const checkoutOrder = {};
    if (composite.paymentSelection) {
      checkoutOrder.paymentMethod =
        composite.paymentSelection === 'transferencia'
          ? 'transferencia'
          : composite.paymentSelection === 'efectivo'
            ? 'efectivo'
            : 'online_manual';
    }
    if (composite.cashAmount != null) checkoutOrder.cash_amount = composite.cashAmount;
    if (composite.address) {
      checkoutOrder.address = composite.address;
      checkoutOrder.serviceType = 'delivery';
    }
    if (composite.tip != null) checkoutOrder.tip = composite.tip;
    if (composite.tipPercent != null) checkoutOrder.tip_percent = composite.tipPercent;

    const finishCompositeOrder = () => {
      try {
        const hasCart = getCartCount() > 0;
        if (hasCart || compositeLineCount > 0) {
          if (composite.unmatchedNotes?.length) {
            addBotMessage(
              `Anoté también: ${composite.unmatchedNotes.join('; ')}. Si hay algo que no está en la carta, lo dejamos en el pedido.`
            );
          }
          if (hasCart) {
            showOrderDetailAndFollowUp(checkoutOrder);
          } else {
            sessionMemoryRef.current.pendingCheckoutPrefill = {
              ...(sessionMemoryRef.current.pendingCheckoutPrefill || {}),
              ...checkoutOrder,
            };
            addBotMessage(
              'Listo, armé tu pedido. Si no ves el resumen, tocá «Terminar pedido» arriba para confirmar.'
            );
          }
          return;
        }
        if (composite.unmatchedNotes?.length) {
          addBotMessage(
            `Entendí esto pero no lo ubiqué en la carta: ${composite.unmatchedNotes.join('; ')}. ¿Podés decirme el nombre exacto del producto?`
          );
          return;
        }
        if (composite.address || composite.paymentSelection) {
          addBotMessage(
            'Entendí dirección o forma de pago, pero todavía no hay productos en el pedido. Decime qué querés — por ejemplo: "quiero 2 hamburguesas y 1 papas".'
          );
          return;
        }
        addBotMessage(
          'Entendí tu pedido pero no pude armarlo en el carrito. Decime los productos de nuevo, uno por uno.'
        );
      } catch (err) {
        console.error('assistant composite order failed', err);
        addBotMessage(
          'Tuve un problema al procesar tu pedido. Probá de nuevo o tocá «Terminar pedido» para continuar.'
        );
      }
    };

    if (compositeLineCount > 0) {
      setTimeout(finishCompositeOrder, 0);
    } else {
      finishCompositeOrder();
    }
  };

  const handleKnowOrderMessage = (q) => {
    if (!menuData?.products?.length) return false;
    const mem = sessionMemoryRef.current;
    if (mem.assistantEntryMode !== 'know-order' && !mem.guidedOrderActive) return false;

    const draft = mem.guidedOrderDraft || createEmptyGuidedOrderDraft();
    const guidedResult = processGuidedOrderTurn({ draft, message: q, menuData });

    const tryFinalize = (text) => {
      const staffParsed = parseStaffTakeOrderText(text, menuData.products, menuData);
      if (!staffParsed?.cartItems?.length) return false;
      mem.guidedOrderActive = false;
      mem.guidedOrderDraft = null;
      mem.assistantEntryMode = null;
      if (typeof setCurrentStep === 'function') setCurrentStep('order');
      applyStaffParsedOrder(staffParsed);
      return true;
    };

    if (guidedResult.action === 'full_parse' || guidedResult.action === 'finalize') {
      if (tryFinalize(guidedResult.text)) return true;
    }

    mem.guidedOrderDraft = guidedResult.draft;
    mem.guidedOrderActive = true;
    mem.assistantEntryMode = 'know-order';
    if (typeof setCurrentStep === 'function') setCurrentStep('guided-order');

    if (guidedResult.action === 'need_products' || guidedResult.action === 'prompt') {
      addBotMessage(guidedResult.prompt, [
        {
          label: 'Ver menú',
          value: 'guided-menu',
          icon: Menu,
          action: () => {
            addUserMessage('Ver menú');
            if (typeof showOrderOptions === 'function') showOrderOptions();
          },
        },
      ]);
      return true;
    }

    if (guidedResult.action === 'full_parse' || guidedResult.action === 'finalize') {
      addBotMessage(
        'No ubiqué productos en la carta con lo que llevamos. Decime cantidad + nombre como en el menú, o tocá «Ver menú».'
      );
      return true;
    }

    return false;
  };

  const openBusinessWhatsApp = () => {
    const business = menuData?.business || {};
    if (!business.whatsapp) {
      logStructured('analytics', {
        event: 'whatsapp_failed',
        businessId,
        source: 'assistant_consult',
        reason: 'no_business_whatsapp',
      });
      toast.error('Este local no tiene WhatsApp cargado.');
      return;
    }
    const whatsappNumber = String(business.whatsapp).replace(/\D/g, '');
    const message = encodeURIComponent(
      `Hola, quería consultar por ${business.name || 'el local'}`
    );
    openWhatsAppUrl(`https://wa.me/${whatsappNumber}?text=${message}`, {
      businessId,
      source: 'assistant_consult',
    });
  };

  const { prioritizeFallbackKeysForMemory, optionsFromFreeTextFallback } = createAssistantFallbackOptions({
    getSessionMemory: () => sessionMemoryRef.current,
    getMenuData: () => menuData,
    assistantAnalyticsCtx,
    openBusinessWhatsApp,
    showMainMenu,
    handleMainChoiceFromText,
    handleOrderChoice,
    handleOrderChoiceFromText,
    getCartCount,
    touchCheckoutFromAssistant,
    openAssistantCheckout,
    assistantServicePrefKey: ASSISTANT_SERVICE_PREF_KEY,
  });

  const scheduleSilentNudge = (botTsMs) => {
    if (silentNudgeTimeoutRef.current) clearTimeout(silentNudgeTimeoutRef.current);
    const delayMs = 16000 + Math.floor(Math.random() * 11000);
    silentNudgeTimeoutRef.current = setTimeout(() => {
      silentNudgeTimeoutRef.current = null;
      const m = messagesRef.current;
      if (!m.length) return;
      const last = m[m.length - 1];
      if (last.type !== 'bot' || !last.products?.length) return;
      if (last.timestamp?.getTime?.() !== botTsMs) return;
      const hasNewerUser = m.some(
        (msg) => msg.type === 'user' && msg.timestamp.getTime() > last.timestamp.getTime()
      );
      if (hasNewerUser) return;
      if (last.silentNudgeSent) return;
      const nudge = buildContextualNudge({
        sessionMemory: { ...sessionMemoryRef.current },
        lastProducts: sessionMemoryRef.current.lastProducts || [],
        lastIntent: sessionMemoryRef.current.lastIntent,
        menuData,
      });
      if (nudge.abTracking) {
        trackAssistantEvent('assistant_silent_nudge_shown', {
          ...assistantAnalyticsCtx(null),
          detected_intent: 'silent_nudge',
          ab_test_key: nudge.abTracking.ab_test_key,
          ab_variant_id: nudge.abTracking.ab_variant_id,
        });
      }
      setMessages((prev) => {
        const L = prev[prev.length - 1];
        if (!L || L.type !== 'bot' || L.timestamp?.getTime() !== botTsMs) return prev;
        if (L.silentNudgeSent) return prev;
        const marked = prev.map((x, i) =>
          i === prev.length - 1 ? { ...x, silentNudgeSent: true } : x
        );
        let keys = prioritizeFallbackKeysForMemory(nudge.fallbackKeys || []);
        let opts = optionsFromFreeTextFallback(keys);
        if (!opts.length) {
          opts = [
            {
              label: 'Ver recomendaciones',
              value: 'sn-more',
              icon: Menu,
              action: () => handleMainChoiceFromText('order'),
            },
            {
              label: 'Ver lo que llevás',
              value: 'sn-view-cart',
              icon: ShoppingCart,
              action: () => showCartSummaryInChat(),
            },
            {
              label: 'Completa tus datos para terminar el pedido',
              value: 'sn-cart',
              icon: MessageCircle,
              action: () => proceedToCheckoutRef.current(),
            },
            {
              label: 'Al inicio',
              value: 'sn-home',
              icon: ChevronRight,
              action: () => showMainMenuRef.current(),
            },
          ];
        }
        return [
          ...marked,
          {
            type: 'bot',
            text: nudge.text,
            options: opts,
            products: nudge.anchorProducts?.length ? nudge.anchorProducts : undefined,
            textAfterProducts: nudge.textAfterProducts || undefined,
            suggestedProducts: nudge.suggestedProducts?.length ? nudge.suggestedProducts : undefined,
            silentNudgeSent: false,
            timestamp: new Date(),
          },
        ];
      });
    }, delayMs);
  };

  const deriveLastContextProductIdsFromMessages = () => {
    const m = messagesRef.current;
    for (let i = m.length - 1; i >= 0; i--) {
      const msg = m[i];
      if (msg.type !== 'bot') continue;
      if (msg.productCardMode === 'confirmation') continue;
      const fromMain = (msg.products || []).map((p) => p.id).filter(Boolean);
      const fromSug = (msg.suggestedProducts || []).map((p) => p.id).filter(Boolean);
      const ids = [...new Set([...fromMain, ...fromSug])];
      if (ids.length) return ids;
    }
    return [];
  };

  const resolveContextProductIdsForFreeText = () => {
    const fromMem = sessionMemoryRef.current.lastProducts || [];
    const fromRef = lastSuggestedProductIdsRef.current || [];
    if (fromMem.length) return [...fromMem];
    if (fromRef.length) return [...fromRef];
    const derived = deriveLastContextProductIdsFromMessages();
    if (derived.length) {
      sessionMemoryRef.current.lastProducts = derived;
      lastSuggestedProductIdsRef.current = derived;
    }
    return derived;
  };

  const dispatchFreeTextAnswer = (ans, meta = { isFirst: true }) => {
    if (!ans) return;
    if (meta.isFirst && ans.orderNoteAppend) {
      appendOrderNotes(businessId, ans.orderNoteAppend);
    }
    let text = ans.text;
    const fr =
      meta.isFirst && !ans.containmentIncluded ? ans.frustrationLevel || 0 : 0;
    if (fr > 0 && text) {
      text = `${frustrationPrefix(fr)}\n\n${text}`;
    } else if (fr > 0 && !text) {
      text = frustrationPrefix(fr);
    }

    let keys = [...(ans.fallbackKeys || [])];
    if (meta.isFirst && fr >= 2 && menuData?.business?.whatsapp) {
      const pref = ['whatsapp', 'order_hint', 'catalog', 'main'];
      keys = [...pref, ...keys.filter((k) => !pref.includes(k))];
    }
    keys = prioritizeFallbackKeysForMemory(keys);
    const opts = optionsFromFreeTextFallback(keys);

    if (ans.products?.length) {
      const ab = ans.assistantAbAnalytics;
      trackAssistantEvent('assistant_products_shown', {
        ...assistantAnalyticsCtx(null),
        detected_intent: inferAssistantIntent(ans),
        product_ids_shown: ans.products.map((p) => p.id).filter(Boolean),
        suggested_product_ids_shown: (ans.suggestedProducts || [])
          .map((p) => p.id)
          .filter(Boolean),
        ...(ab && ab.ab_test_key && ab.ab_variant_id
          ? { ab_test_key: ab.ab_test_key, ab_variant_id: ab.ab_variant_id }
          : {}),
      });
      sessionMemoryRef.current.lastProducts = ans.products.map((p) => p.id).filter(Boolean);
      lastSuggestedProductIdsRef.current = ans.products.map((p) => p.id).filter(Boolean);
      if (ans.suggestedProducts?.length) {
        const sid = ans.suggestedProducts.map((p) => p.id).filter(Boolean);
        sessionMemoryRef.current.shownContextualProductIds = [
          ...new Set([...(sessionMemoryRef.current.shownContextualProductIds || []), ...sid]),
        ].slice(-40);
      }
      if (ans.suggestedProducts?.length) {
        trackAssistantEvent('assistant_contextual_suggestions_shown', {
          ...assistantAnalyticsCtx(null),
          detected_intent: inferAssistantIntent(ans),
          product_ids_shown: ans.products.map((p) => p.id).filter(Boolean),
          suggested_product_ids_shown: ans.suggestedProducts.map((p) => p.id).filter(Boolean),
        });
      }
      addBotMessage(text, opts.length ? opts : null, ans.products, {
        textAfterProducts: ans.textAfterProducts,
        suggestedProducts: ans.suggestedProducts,
      });
      scheduleSilentNudge(Date.now());
    } else if (text) {
      addBotMessage(text, opts.length ? opts : null);
    } else if (opts.length) {
      addBotMessage(null, opts);
    }

    if (meta.isFirst && ans.orderChoice) {
      handleOrderChoiceFromText(ans.orderChoice);
    }

    if (ans.followUp) {
      setTimeout(() => dispatchFreeTextAnswer(ans.followUp, { isFirst: false }), 1000);
    }
  };

  const submitFreeText = () => {
    const q = freeTextInput.trim();
    if (!q) return;
    if (isDemoBusinessId(businessId)) {
      if (!demoAssistantUsedRef.current) {
        demoAssistantUsedRef.current = true;
        trackAssistantUsed(businessId);
      }
    } else {
      trackEvent('assistant_message_sent', { business_id: businessId });
    }
    setFreeTextInput('');
    addUserMessage(q);
    setTimeout(() => {
      try {
      if (isDemoBusinessId(businessId) && menuData) {
        const raw = buildDemoAssistantFreeTextAnswer(menuData, q);
        trackAssistantEvent('user_message_submitted', assistantAnalyticsCtx(null));
        dispatchFreeTextAnswer(raw);
        return;
      }

      const nq = normalizeAssistantText(q);
      updateSessionMemoryFromMessage(sessionMemoryRef, q, nq);

      if (handleOrderCloseFreeText?.(q)) return;

      if (matchesOrderCorrectionIntent(q)) {
        addBotMessage('Sí, podés cambiar. ¿Qué querés modificar o sacar del pedido?');
        return;
      }

      if (matchesOrderDetailRequest(q)) {
        if (getCartCount() <= 0) {
          addBotMessage('Todavía no hay nada en tu pedido. Decime qué querés — por ejemplo: "quiero una hamburguesa sin mayonesa y bebida".');
        } else {
          const preview = buildCheckoutPreview();
          addBotMessage('Este es tu pedido:', null, null, { orderSummary: preview });
        }
        return;
      }

      if (matchesCheckoutReadyIntent(q) && getCartCount() > 0) {
        showCheckoutPreferencePrompt();
        return;
      }

      if (MENU_INTENT_RX.test(nq)) {
        handleMainChoiceFromText('browse-menu');
        return;
      }
      if (KNOW_ORDER_INTENT_RX.test(nq)) {
        handleMainChoiceFromText('know-order');
        return;
      }

      const mem = sessionMemoryRef.current;
      const entryMode = mem.assistantEntryMode;
      const inKnowOrder = entryMode === 'know-order' || mem.guidedOrderActive;
      const compositeOpts = inKnowOrder ? { staffOrder: true } : undefined;

      if (menuData?.products?.length) {
        if (looksLikeStaffOrderPaste(q) || (inKnowOrder && q.includes('\n'))) {
          const staffParsed = parseStaffTakeOrderText(q, menuData.products, menuData);
          if (staffParsed?.cartItems?.length) {
            applyStaffParsedOrder(staffParsed);
            return;
          }
        }

        const orderLike =
          hasCompositeOrderIntent(q) ||
          looksLikeStaffOrderPaste(q) ||
          ORDER_LINE_HINT_RX.test(q) ||
          looksLikeProductOrderLine(q, menuData.products, menuData);

        if (orderLike) {
          const earlyComposite = parseCompositeOrderText(
            q,
            menuData.products,
            menuData,
            compositeOpts
          );
          if (
            earlyComposite?.lines?.length ||
            earlyComposite?.unmatchedNotes?.length ||
            earlyComposite?.address ||
            earlyComposite?.paymentSelection
          ) {
            mem.assistantEntryMode = null;
            mem.guidedOrderActive = false;
            mem.guidedOrderDraft = null;
            applyCompositeParsedOrder(earlyComposite);
            return;
          }
        }
      }

      if (handleKnowOrderMessage(q)) return;

      if (inKnowOrder && menuData?.products?.length) {
        const staffParsed = parseStaffTakeOrderText(q, menuData.products, menuData);
        if (staffParsed?.cartItems?.length) {
          applyStaffParsedOrder(staffParsed);
          return;
        }
        if (staffParsed && !staffParsed?.cartItems?.length && looksLikeStaffOrderPaste(q)) {
          addBotMessage(
            staffParsed.unmatchedNotes?.length
              ? `No ubiqué estos productos en la carta: ${staffParsed.unmatchedNotes.join('; ')}. Revisá los nombres o tocá «Ver menú».`
              : 'No pude armar el pedido con ese texto. Revisá cantidad + nombre en cada línea, como en el ejemplo.'
          );
          return;
        }
      }

      const composite = parseCompositeOrderText(q, menuData?.products || [], menuData);
      if (composite?.lines?.length || composite?.unmatchedNotes?.length || composite?.address || composite?.paymentSelection) {
        applyCompositeParsedOrder(composite);
        return;
      }

      if (hasOrderInstructionIntent(nq)) {
        addBotMessage(
          'Entendí que querés pedir, pero no ubiqué el producto en la carta. Decime el nombre tal como sale en el menú — por ejemplo: "2 hamburguesas sin cebolla y 1 papas".'
        );
        return;
      }

      const storedRaw =
        typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(ASSISTANT_SERVICE_PREF_KEY) : null;
      const storedPref =
        storedRaw === 'delivery' ? 'delivery' : storedRaw === 'pickup' ? 'pickup' : null;
      if (!sessionMemoryRef.current.servicePreference && storedPref) {
        sessionMemoryRef.current.servicePreference = storedPref;
      }
      const ctxPids = resolveContextProductIdsForFreeText();
      const raw = buildFreeTextAnswer(menuData, q, {
        flowContext: {
          currentStep,
          servicePreference: storedPref,
          lastSuggestedProductIds: ctxPids,
          sessionMemory: { ...sessionMemoryRef.current, lastProducts: ctxPids },
          cartItemCount: getCartCount(),
        },
      });
      trackAssistantEvent('user_message_submitted', assistantAnalyticsCtx(null));
      const resolvedIntent = inferAssistantIntent(raw);
      trackAssistantEvent('assistant_intent_resolved', {
        ...assistantAnalyticsCtx(q),
        detected_intent: resolvedIntent,
        product_ids_shown: (raw.products || []).map((p) => p.id).filter(Boolean),
        suggested_product_ids_shown: (raw.suggestedProducts || [])
          .map((p) => p.id)
          .filter(Boolean),
        had_products: !!(raw.products && raw.products.length),
        had_suggested_products: !!(raw.suggestedProducts && raw.suggestedProducts.length),
        order_choice: raw.orderChoice || null,
      });
      if (resolvedIntent === 'fallback_generic') {
        trackAssistantEvent('assistant_fallback_used', {
          ...assistantAnalyticsCtx(q),
          detected_intent: resolvedIntent,
        });
      }
      const inferred = inferServicePreferenceFromText(nq);
      if (inferred && typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(ASSISTANT_SERVICE_PREF_KEY, inferred);
        sessionMemoryRef.current.servicePreference = inferred;
      }
      const detected = detectFrustrationLevel(nq);
      raw.frustrationLevel = raw.containmentIncluded ? 0 : detected;
      dispatchFreeTextAnswer(raw);
      } catch (err) {
        console.error('assistant submitFreeText failed', err);
        addBotMessage(
          'Tuve un problema al leer tu mensaje. Probá de nuevo o tocá «Terminar pedido» para continuar.'
        );
      }
    }, 240);
  };

  return {
    submitFreeText,
    dispatchFreeTextAnswer,
    scheduleSilentNudge,
  };
}
